import clsx from 'clsx';
import _ from 'lodash';
import { Field, useForm, useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { IconSelect } from '@/core/IconSelect.molecule';
import { FormFieldWrapper } from '@/formbuilder/FormFieldWrapper';
import { getFormFieldProps, getFormFieldWrapperProps } from '@/formbuilder/formbuilder.utilities';
import { InputGroup } from '@seeqdev/qomponents';
import type { ReactSelectOption } from '@/core/IconSelect.molecule';
import type { ValidatingFormComponent } from '@/formbuilder/formBuilder.types';

export interface IconSelectProps extends ValidatingFormComponent<any> {
  component: 'IconSelectFormComponent';
  selectOptions: ReactSelectOption[];
  onChange: (option: { value: any }, property?: string) => void;
  onBlur?: any;
  formattedOptions?: boolean;
  placeholder?: string;
  testId?: string;
  insideModal?: boolean;
  appendToBody?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  wrapperClasses?: string;
  isMultipleSelect?: boolean;
  isSearchable?: boolean;
  onRemove?: (option: { value: any }) => void;
  // if true component will not be memoized. this should be used SPARINGLY. Should only be necessary if you need to
  // access state that would be stale if your component is memoized (see PeriodicCondition)
  skipMemo?: boolean;
  onTooltipClick?: () => void;
  creatable?: boolean;
  labelClassNames?: string;
  removeAllEnabled?: boolean;
  onRemoveAllClick?: () => void;
  removeAllLinkClassNames?: string;
  addItemsEnabled?: boolean;
  addItemsLabel?: string;
  onAddItemsClick?: () => void;
  showAddButton?: boolean;
  onClickAddButton?: () => void;
  addButtonTooltip?: string;
  textEntryAllowed?: boolean;
  textToggleDisabled?: boolean;
  showEditButton?: boolean;
  onClickEditButton?: () => void;
  editButtonTooltip?: string;
}

export const IconSelectFormComponent = (props: IconSelectProps) => {
  const {
    name,
    validation,
    testId = 'iconSelect',
    wrapperClassNames,
    customErrorText,
    labelClassNames,
    showAddButton = false,
    showEditButton = false,
    onClickAddButton,
    onClickEditButton,
    addButtonTooltip,
    editButtonTooltip,
  } = props;
  const { t } = useTranslation();

  const defaultValidation = _.isEmpty;
  const form = useForm();
  const formState = form.getState();
  const { errors, hasValidationErrors, modified, submitFailed } = useFormState({
    subscription: { errors: true, hasValidationErrors: true, modified: true, submitFailed: true },
  });
  const showError = _.has(errors, name) && hasValidationErrors && (modified?.[name] || submitFailed);

  return (
    <FormFieldWrapper
      {...getFormFieldWrapperProps(props, ['wrapperClassNames', 'testId'])}
      testId={testId}
      labelClassNames={labelClassNames}
      wrapperClassNames={clsx('flexFill', wrapperClassNames)}
      showError={showError}
      customErrorText={customErrorText}>
      <Field name={name} validate={validation || defaultValidation}>
        {({ input, meta }) => {
          const formFieldProps = getFormFieldProps(formState, input, meta, props);
          if (!showEditButton && !showAddButton) {
            return <IconSelect {...formFieldProps} testId={`${testId}Field`} />;
          } else {
            return (
              <InputGroup
                showError={formFieldProps.showError}
                field={
                  <IconSelect
                    {...formFieldProps}
                    testId={`${testId}Field`}
                    wrapperClasses={clsx('flexFill tw:w-full')}
                    controlClasses={clsx('no-border')}
                  />
                }
                append={[
                  showAddButton
                    ? {
                        variant: 'button',
                        buttonProps: {
                          icon: 'fa-plus',
                          iconStyle: 'theme',
                          testId: `${testId}AddButton`,
                          onClick: () => {
                            if (onClickAddButton) {
                              onClickAddButton();
                            }
                          },
                          tooltip: t(addButtonTooltip ?? ''),
                        },
                      }
                    : undefined,
                  showEditButton
                    ? {
                        variant: 'button',
                        buttonProps: {
                          icon: 'fa-pencil',
                          iconStyle: 'theme',
                          testId: `${testId}EditButton`,
                          onClick: () => {
                            if (onClickEditButton) {
                              onClickEditButton();
                            }
                          },
                          tooltip: t(editButtonTooltip ?? ''),
                        },
                      }
                    : undefined,
                ]}
              />
            );
          }
        }}
      </Field>
    </FormFieldWrapper>
  );
};
